'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import Link from 'next/link'
import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import type { z } from 'zod'
import PasswordRequirements from '@/components/Auth/PasswordRequirements'
import { REGISTER_COMPANY_FIELD_MAP } from '@/constants/auth'
import { CAREER_PJ_OPTIONS } from '@/constants/customer'
import { CELL_PHONE_MASK, CNPJ_MASK } from '@/constants/masks'
import { useRegister } from '@/hooks/business/auth/useRegister'
import { useFormAction } from '@/hooks/shared/useFormAction'
import { type IRegisterCompanyForm, RegisterCompanyFormSchema } from '@/schemas/auth/authFormSchemas'
import Button from '~/components/Form/Button'
import Checkbox from '~/components/Form/Checkbox'
import Input from '~/components/Form/Input'
import MaskedInput from '~/components/Form/MaskedInput'
import Select from '~/components/Form/Select'

type IRegistration = 'inscricaoMunicipal' | 'inscricaoEstadual'

const EXEMPT = 'Isento'

type IFormInput = z.input<typeof RegisterCompanyFormSchema>
/**
 * Cadastro de pessoa jurídica (`/cadastro`): abre a sessão e vai para a home; erro de e-mail, CNPJ ou razão social
 * aparece no campo. "Sou isento" grava "Isento" na inscrição e devolve o que estava digitado ao desmarcar, revalidando
 * a inscrição quando já há tentativa de cadastro.
 */
const CompanySignUpForm: React.FC = () => {
    const {
        control,
        register,
        handleSubmit,
        setError,
        getValues,
        setValue,
        trigger,
        formState: { errors, isSubmitted },
    } = useForm<IFormInput, unknown, IRegisterCompanyForm>({
        resolver: zodResolver(RegisterCompanyFormSchema),
        defaultValues: {
            cnpj: '',
            corporateName: '',
            inscricaoMunicipal: '',
            inscricaoEstadual: '',
            email: '',
            companyPhone: '',
            password: '',
            confirmPassword: '',
            terms: false,
        },
    })

    const { registerCompany } = useRegister()
    // O handleSubmit entrega o valor já validado (a saída do schema); o tipo do formulário é o da entrada.
    const { handler } = useFormAction((form: IFormInput) => registerCompany(form as IRegisterCompanyForm), {
        setError,
        getValues,
        fieldMap: REGISTER_COMPANY_FIELD_MAP,
    })

    const [exempt, setExempt] = useState<Record<IRegistration, boolean>>({
        inscricaoMunicipal: false,
        inscricaoEstadual: false,
    })
    const [typed, setTyped] = useState<Record<IRegistration, string>>({ inscricaoMunicipal: '', inscricaoEstadual: '' })

    const toggleExempt = (field: IRegistration) => {
        if (exempt[field]) {
            setValue(field, typed[field])
        } else {
            setTyped((previous) => ({ ...previous, [field]: getValues(field) ?? '' }))
            setValue(field, EXEMPT)
        }
        setExempt((previous) => ({ ...previous, [field]: !previous[field] }))
        // O setValue não revalida: sem isso a mensagem de "informe a inscrição" ficava na tela depois de marcar "Sou isento".
        // Antes da primeira tentativa de cadastro não há mensagem para atualizar, e validar agora seria cobrar cedo demais.
        if (isSubmitted || errors[field]) trigger(field)
    }

    const registrationField = (field: IRegistration, labelText: string) => (
        <div className="flex flex-col items-start gap-2 w-full">
            <Input
                register={register(field)}
                error={errors[field]?.message}
                labelText={labelText}
                type="number"
                placeholder={exempt[field] ? EXEMPT : ''}
                inputClass={exempt[field] ? 'cursor-not-allowed' : ''}
                readOnly={exempt[field]}
                className="w-full"
            />
            <Checkbox
                name={`${field}Exempt`}
                checked={exempt[field]}
                onChange={() => toggleExempt(field)}
                className="flex-col items-center"
                labelText={<span className="text-sm text-primary font-sora text-center align-middle">Sou isento</span>}
            />
        </div>
    )

    return (
        <div className="container">
            <form onSubmit={handleSubmit(handler)}>
                <div className="flex gap-2 max-lg:flex-col">
                    <div className="flex flex-col gap-2 border border-gray-cart rounded-2xl p-4 lg:w-2/3">
                        <div className="flex max-md:flex-col gap-2 w-full">
                            <MaskedInput
                                control={control}
                                name="cnpj"
                                labelText="CNPJ*"
                                mask={CNPJ_MASK}
                                type="number"
                                className="w-full"
                            />

                            <Input
                                register={register('corporateName')}
                                error={errors.corporateName?.message}
                                labelText="Razão Social*"
                                type="text"
                                className="w-full"
                            />
                        </div>

                        <div className="flex max-md:flex-col gap-2 w-full">
                            {registrationField('inscricaoMunicipal', 'Inscr. Municipal (apenas números)*')}
                            {registrationField('inscricaoEstadual', 'Inscr. Estadual (apenas números)*')}
                        </div>

                        <div className="flex max-md:flex-col gap-2 w-full">
                            <Select
                                register={register('occupation')}
                                error={errors.occupation?.message}
                                labelText="Área de atuação*"
                                defaultValue=""
                                options={CAREER_PJ_OPTIONS}
                                className="w-full"
                            />

                            <Input
                                register={register('email')}
                                error={errors.email?.message}
                                labelText="E-mail*"
                                placeholder="Ex.: nome@email.com"
                                type="text"
                                className="w-full"
                            />

                            <MaskedInput
                                control={control}
                                name="companyPhone"
                                labelText="Celular*"
                                placeholder="DDD + número"
                                mask={CELL_PHONE_MASK}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="p-4 border border-gray-cart rounded-2xl">
                        <span className="col-span-2 text-lg text-primary font-sora font-bold">CRIAÇÃO DE SENHA</span>

                        <div className="flex max-lg:flex-col-reverse items-center gap-4">
                            <div className="flex flex-col gap-2 w-full">
                                <Input
                                    register={register('password')}
                                    error={errors.password?.message}
                                    labelText="Senha*"
                                    type="password"
                                    className="w-full"
                                />

                                <Input
                                    register={register('confirmPassword')}
                                    error={errors.confirmPassword?.message}
                                    type="password"
                                    labelText="Confirmar senha*"
                                    className="w-full"
                                />
                            </div>

                            <PasswordRequirements />
                        </div>
                    </div>
                </div>
                <div className="pb-10 lg:mr-auto lg:pl-28 mt-6">
                    <Checkbox
                        name="terms"
                        register={register('terms')}
                        error={errors.terms?.message}
                        className="flex-col"
                        labelText={
                            <span className="ml-2 text-sm text-primary font-sora">
                                Li e aceito os{' '}
                                <Link
                                    href="https://glofi.zendesk.com/hc/pt-br/articles/13294668995483"
                                    className="underline"
                                >
                                    Termos e condições
                                </Link>{' '}
                                e autorizo o uso de meus dados de acordo com a{' '}
                                <Link
                                    href="https://glofi.zendesk.com/hc/pt-br/articles/13294581466395"
                                    className="underline"
                                >
                                    Declaração de privacidade
                                </Link>
                                .
                            </span>
                        }
                    />
                    <Button size="lg" color="tertiary" type="submit">
                        CADASTRAR
                    </Button>
                </div>
            </form>
        </div>
    )
}

export default CompanySignUpForm
